/**
 * WCC360 v2 — SlotVector writer.
 *
 * The confidence rule in one paragraph:
 *   Any slot written by two screens whose values disagree beyond a threshold
 *   is marked slot_confidence: 'low'. It keeps the MORE CONSERVATIVE value
 *   (the lower one — under-tolerance is a welfare risk, not a bonus) and is
 *   surfaced on the review screen. Two agreeing screens raise confidence.
 *
 * We deliberately store contributions per slot (which screen wrote what)
 * so the review screen can show the conflict rather than paper over it.
 */
import type { Learner } from "@/lib/scoring";

/** All slots that Part 2 screens can write into. */
export type SlotKey =
  // Preferences (symmetric fit)
  | "prefOutdoor" | "prefMovement" | "prefScreenTime" | "prefTravel" | "prefStableSchedule"
  | "prefAutonomy" | "prefImpactVisibility" | "prefIncomeUpside" | "prefPrestige" | "prefStability"
  | "prefCreativity" | "prefFocusVsSwitching" | "prefSoloVsTeam"
  // Tolerances (asymmetric fit — conservative on conflict)
  | "tolPublicSpeaking" | "tolPeopleContact" | "tolClosePhysical" | "tolSensory"
  | "tolSocialLoad" | "tolEmotionalLoad" | "tolGruntWork";

/** Every slot key. Iterate this to walk the whole vector. */
export const SLOT_KEYS: readonly SlotKey[] = [
  "prefOutdoor", "prefMovement", "prefScreenTime", "prefTravel", "prefStableSchedule",
  "prefAutonomy", "prefImpactVisibility", "prefIncomeUpside", "prefPrestige", "prefStability",
  "prefCreativity", "prefFocusVsSwitching", "prefSoloVsTeam",
  "tolPublicSpeaking", "tolPeopleContact", "tolClosePhysical", "tolSensory",
  "tolSocialLoad", "tolEmotionalLoad", "tolGruntWork",
];

/** Tolerance slots are asymmetric — conflicts keep the LOWER value. */
export const TOLERANCE_SLOTS: ReadonlySet<SlotKey> = new Set([
  "tolPublicSpeaking", "tolPeopleContact", "tolClosePhysical", "tolSensory",
  "tolSocialLoad", "tolEmotionalLoad", "tolGruntWork",
]);

export interface SlotContribution {
  screenId: string;
  value: number;          // 0..100
  label?: string;         // human-readable "what they said" for the review screen
}

export interface SlotState {
  contribs: Record<string, SlotContribution[]>;
  values: Partial<Record<SlotKey, number>>;
  lowConfidence: Partial<Record<SlotKey, true>>;
}

export function emptySlots(): SlotState {
  return { contribs: {}, values: {}, lowConfidence: {} };
}

export interface WriteOpts {
  /** Disagreement threshold — above this the slot flips to low-confidence. */
  disagreeThreshold?: number;
  /** Screen-specific human-readable summary of what the learner said. */
  label?: string;
  /** Force conservative-min resolution even for pref slots. */
  forceConservative?: boolean;
}

/**
 * Write (or overwrite) one screen's contribution to a slot and recompute
 * the aggregate. Overwriting the same screen replaces its contribution —
 * we never double-count when the learner goes back and changes an answer.
 */
export function writeSlot(
  s: SlotState, key: SlotKey, screenId: string, value: number, opts: WriteOpts = {},
): SlotState {
  const threshold = opts.disagreeThreshold ?? 35;
  const clean = (s.contribs[key] ?? []).filter((c) => c.screenId !== screenId);
  const contribs = [...clean, { screenId, value, label: opts.label }];

  let finalValue = value;
  let low = false;

  if (contribs.length >= 2) {
    const vals = contribs.map((c) => c.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const disagree = max - min > threshold;
    const isTol = TOLERANCE_SLOTS.has(key);
    if (disagree || opts.forceConservative || isTol) {
      // Conservative: for tolerance and disagreement, take the lower.
      // For agreeing preferences, still average.
      if (disagree || opts.forceConservative || isTol) {
        finalValue = disagree || opts.forceConservative
          ? min
          : Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
      }
      low = disagree;
    } else {
      finalValue = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    }
  }

  const nextLow = { ...s.lowConfidence };
  if (low) nextLow[key] = true; else delete nextLow[key];

  return {
    contribs: { ...s.contribs, [key]: contribs },
    values: { ...s.values, [key]: finalValue },
    lowConfidence: nextLow,
  };
}

/** Fold a whole map of slot writes in one go. */
export function writeMany(
  s: SlotState, screenId: string, writes: Partial<Record<SlotKey, number>>, opts: WriteOpts = {},
): SlotState {
  let next = s;
  for (const [k, v] of Object.entries(writes) as [SlotKey, number][]) {
    next = writeSlot(next, k, screenId, v, opts);
  }
  return next;
}

/** Neutral defaults for slots the learner never touched. 50 = "no signal". */
const SLOT_DEFAULT: Record<SlotKey, number> = {
  prefOutdoor: 50, prefMovement: 50, prefScreenTime: 50, prefTravel: 50, prefStableSchedule: 50,
  prefAutonomy: 50, prefImpactVisibility: 50, prefIncomeUpside: 50, prefPrestige: 50,
  prefStability: 50, prefCreativity: 50, prefFocusVsSwitching: 50, prefSoloVsTeam: 50,
  tolPublicSpeaking: 50, tolPeopleContact: 50, tolClosePhysical: 50, tolSensory: 50,
  tolSocialLoad: 50, tolEmotionalLoad: 50, tolGruntWork: 50,
};

/** Read a slot for downstream use — defaults to neutral 50 when missing. */
export function slotValue(s: SlotState, key: SlotKey): number {
  return s.values[key] ?? SLOT_DEFAULT[key];
}

/** Assemble the slot part of a Learner. Non-slot fields (archetype,
 *  salaryFloorLpa, studyCapacity, ...) are supplied by the caller. */
export function slotsToLearnerPartial(s: SlotState): Pick<Learner,
  | "prefOutdoor" | "prefMovement" | "prefScreenTime" | "prefTravel" | "prefStableSchedule"
  | "prefAutonomy" | "prefImpactVisibility" | "prefIncomeUpside" | "prefPrestige" | "prefStability"
  | "prefCreativity" | "prefFocusVsSwitching" | "prefSoloVsTeam"
  | "tolPublicSpeaking" | "tolPeopleContact" | "tolClosePhysical" | "tolSensory"
  | "tolSocialLoad" | "tolEmotionalLoad" | "tolGruntWork"
> {
  const g = (k: SlotKey) => slotValue(s, k);
  return {
    prefOutdoor: g("prefOutdoor"), prefMovement: g("prefMovement"),
    prefScreenTime: g("prefScreenTime"), prefTravel: g("prefTravel"),
    prefStableSchedule: g("prefStableSchedule"),
    prefAutonomy: g("prefAutonomy"), prefImpactVisibility: g("prefImpactVisibility"),
    prefIncomeUpside: g("prefIncomeUpside"), prefPrestige: g("prefPrestige"),
    prefStability: g("prefStability"), prefCreativity: g("prefCreativity"),
    prefFocusVsSwitching: g("prefFocusVsSwitching"), prefSoloVsTeam: g("prefSoloVsTeam"),
    tolPublicSpeaking: g("tolPublicSpeaking"), tolPeopleContact: g("tolPeopleContact"),
    tolClosePhysical: g("tolClosePhysical"), tolSensory: g("tolSensory"),
    tolSocialLoad: g("tolSocialLoad"), tolEmotionalLoad: g("tolEmotionalLoad"),
    tolGruntWork: g("tolGruntWork"),
  };
}
